import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Immunizations subsection adapter
 * Handles vaccine/date pairs for various immunizations (13 fields)
 * This follows the MultiColumn pattern: vaccine + date pairs
 */
export class ImmunizationsAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hepatitis B vaccine/date pair
    const hepB = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B
    )?.at(0)

    const hepBDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE
    )?.at(0)

    // Meningococcal vaccine/date pair
    const meningococcal = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL
    )?.at(0)

    const meningococcalDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE
    )?.at(0)

    // COVID-19 vaccine/date pair
    const covid19 = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19
    )?.at(0)

    const covid19Date = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE
    )?.at(0)

    // DTaP vaccine/date pair
    const dtap = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP
    )?.at(0)

    const dtapDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE
    )?.at(0)

    // HPV vaccine/date pair
    const hpv = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV
    )?.at(0)

    const hpvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE
    )?.at(0)

    // RSV vaccine/date pair
    const rsv = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV
    )?.at(0)

    const rsvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE
    )?.at(0)

    // Notes field
    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES
    )?.at(0)

    return {
      [RocFormField.IMMUNIZATIONS_HEP_B]: hepB,
      [RocFormField.IMMUNIZATIONS_HEP_B_DATE]: hepBDate,
      [RocFormField.IMMUNIZATIONS_MENINGOCOCCAL]: meningococcal,
      [RocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE]: meningococcalDate,
      [RocFormField.IMMUNIZATIONS_COVID_19]: covid19,
      [RocFormField.IMMUNIZATIONS_COVID_19_DATE]: covid19Date,
      [RocFormField.IMMUNIZATIONS_DTAP]: dtap,
      [RocFormField.IMMUNIZATIONS_DTAP_DATE]: dtapDate,
      [RocFormField.IMMUNIZATIONS_HPV]: hpv,
      [RocFormField.IMMUNIZATIONS_HPV_DATE]: hpvDate,
      [RocFormField.IMMUNIZATIONS_RSV]: rsv,
      [RocFormField.IMMUNIZATIONS_RSV_DATE]: rsvDate,
      [RocFormField.IMMUNIZATIONS_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_HEP_B)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_HEP_B)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_HEP_B_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_HEP_B_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_MENINGOCOCCAL)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_MENINGOCOCCAL)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_COVID_19)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_COVID_19)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_COVID_19_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_COVID_19_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_DTAP)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_DTAP)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_DTAP_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_DTAP_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_HPV)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_HPV)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_HPV_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_HPV_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_RSV)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_RSV)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_RSV_DATE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_RSV_DATE)
      })
    }

    if (this.formDataFieldExists(RocFormField.IMMUNIZATIONS_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES,
        value: this.getFormDataField(RocFormField.IMMUNIZATIONS_NOTES)
      })
    }

    return mutations
  }
}
